// src/pages/dealer/DealerMyCarListings.jsx

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { dealerAPI } from '../../services/dealerAPI';
import ListingCreditsModal from '../../components/dealer/ListingCreditsModal';

const fmt = (n) => n ? `₹${Number(n).toLocaleString('en-IN')}` : '—';

const STATUS_CONFIG = {
  draft:     { label: 'Draft',     bg: '#f3f4f6', color: '#374151' },
  submitted: { label: 'Under Review', bg: '#dbeafe', color: '#1d4ed8' },
  approved:  { label: 'Approved',  bg: '#dcfce7', color: '#166534' },
  rejected:  { label: 'Rejected',  bg: '#fee2e2', color: '#991b1b' },
  published: { label: 'Live 🟢',   bg: '#dcfce7', color: '#166534' },
};

export default function DealerMyCarListings() {
  const navigate           = useNavigate();
  const [listings, setListings] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [credits,  setCredits]  = useState(0);
  const [showBuy,  setShowBuy]  = useState(false);

  const load = () => {
    setLoading(true);
    Promise.all([
      dealerAPI.getMyCarListings(),
      dealerAPI.getListingCreditPlans(),
    ])
      .then(([lr, cr]) => {
        setListings(lr.data?.data || []);
        setCredits(cr.data?.available_credits ?? 0);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  return (
    <div style={{ fontFamily: "'DM Sans', sans-serif" }}>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: 26, color: '#003566', margin: '0 0 4px' }}>My Car Listings</h1>
          <p style={{ color: '#6b7280', fontSize: 14, margin: 0 }}>Cars you've submitted for the platform</p>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <div style={{
            padding: '8px 14px', background: credits > 0 ? '#f0fdf4' : '#fff1f1',
            border: `1px solid ${credits > 0 ? '#86efac' : '#fca5a5'}`, borderRadius: 8,
            fontSize: 13, fontWeight: 700, color: credits > 0 ? '#166534' : '#dc2626',
          }}>
            🏷️ {credits} credit{credits !== 1 ? 's' : ''}
          </div>
          <button
            onClick={() => setShowBuy(true)}
            style={{ padding: '8px 16px', background: '#f3f4f6', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer', color: '#003566' }}
          >
            + Buy Credits
          </button>
          <button
            onClick={() => navigate('/dealer/sell-car')}
            style={{ padding: '10px 20px', background: 'linear-gradient(135deg,#003566,#001d3d)', border: '2px solid #FFD60A', borderRadius: 10, color: '#FFD60A', fontWeight: 800, fontSize: 14, cursor: 'pointer' }}
          >
            + List New Car
          </button>
        </div>
      </div>

      {loading && (
        <div style={{ textAlign: 'center', padding: '60px 0' }}>
          <div style={{ width: 40, height: 40, border: '4px solid #e0e0e0', borderTopColor: '#003566', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto' }} />
        </div>
      )}

      {!loading && listings.length === 0 && (
        <div style={{ textAlign: 'center', padding: '80px 20px', background: '#fff', borderRadius: 16, boxShadow: '0 2px 12px rgba(0,53,102,0.08)' }}>
          <p style={{ fontSize: 48, margin: '0 0 12px' }}>🚗</p>
          <p style={{ fontWeight: 700, color: '#003566', fontSize: 16, margin: '0 0 6px' }}>No car listings yet</p>
          <p style={{ color: '#9ca3af', fontSize: 14, margin: '0 0 20px' }}>Submit your first car to get started.</p>
          <button
            onClick={() => navigate('/dealer/sell-car')}
            style={{ padding: '12px 28px', background: 'linear-gradient(135deg,#003566,#001d3d)', border: '2px solid #FFD60A', borderRadius: 10, color: '#FFD60A', fontWeight: 800, fontSize: 14, cursor: 'pointer' }}
          >
            + Submit Your Car
          </button>
        </div>
      )}

      {!loading && listings.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {listings.map(listing => {
            const sc = STATUS_CONFIG[listing.status] || STATUS_CONFIG.draft;
            return (
              <div key={listing.id} style={{ background: '#fff', borderRadius: 14, padding: '20px 24px', boxShadow: '0 2px 10px rgba(0,53,102,0.07)', border: '1px solid #e5e7eb' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
                  <div>
                    <h3 style={{ fontFamily: "'Playfair Display', serif", fontSize: 17, color: '#003566', margin: '0 0 4px' }}>
                      {listing.year} {listing.brand} {listing.model}
                    </h3>
                    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', fontSize: 12, color: '#6b7280', marginBottom: 8 }}>
                      {listing.fuel_type    && <span>⛽ {listing.fuel_type}</span>}
                      {listing.transmission && <span>⚙️ {listing.transmission}</span>}
                      {listing.km_driven    && <span>🛣️ {Number(listing.km_driven).toLocaleString('en-IN')} km</span>}
                      {listing.color        && <span>🎨 {listing.color}</span>}
                    </div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20, background: sc.bg, color: sc.color }}>
                        {sc.label}
                      </span>
                      {listing.asking_price > 0 && (
                        <span style={{ fontSize: 13, fontWeight: 700, color: '#003566' }}>
                          Ask: {fmt(listing.asking_price)}
                        </span>
                      )}
                      {listing.final_sell_price > 0 && (
                        <span style={{ fontSize: 13, fontWeight: 700, color: '#059669' }}>
                          Final: {fmt(listing.final_sell_price)}
                        </span>
                      )}
                    </div>
                    {listing.rejection_reason && (
                      <p style={{ margin: '8px 0 0', fontSize: 12, color: '#dc2626', background: '#fee2e2', padding: '6px 10px', borderRadius: 6 }}>
                        Rejection reason: {listing.rejection_reason}
                      </p>
                    )}
                    {listing.status === 'approved' && listing.car_id && (
                      <p style={{ margin: '8px 0 0', fontSize: 12, color: '#166534' }}>
                        ✅ Car created (ID #{listing.car_id}). Admin is adding details and will publish soon.
                      </p>
                    )}
                  </div>

                  {/* Images */}
                  {listing.images?.length > 0 && (
                    <div style={{ display: 'flex', gap: 6 }}>
                      {listing.images.slice(0, 3).map((src, i) => (
                        <img key={i} src={src} alt="" style={{ width: 72, height: 54, objectFit: 'cover', borderRadius: 8, border: '1px solid #e5e7eb' }} onError={e => e.target.style.display = 'none'} />
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showBuy && (
        <ListingCreditsModal
          onClose={() => setShowBuy(false)}
          onSuccess={() => { load(); setShowBuy(false); }}
        />
      )}
    </div>
  );
}